package com.swetdot.swetgram.ui.chat

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.swetdot.swetgram.domain.MessageReaction
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Motion
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetEasing
import com.swetdot.swetgram.ui.design.SwetPressFeedback
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.design.swetTween
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * A list of reactions under a message bubble.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun MessageReactions(
    reactions: List<MessageReaction>,
    onReactionClick: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    FlowRow(
        modifier = modifier.padding(top = if (reactions.isEmpty()) 0.dp else Space.half),
        horizontalArrangement = Arrangement.spacedBy(Space.half),
        verticalArrangement = Arrangement.spacedBy(Space.half),
    ) {
        reactions.forEach { reaction ->
            key(reaction.emoji) {
                ReactionChip(
                    reaction = reaction,
                    onClick = { onReactionClick(reaction.emoji) },
                )
            }
        }
    }
}

/**
 * One reaction: emoji and count.
 */
@Composable
private fun ReactionChip(
    reaction: MessageReaction,
    onClick: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    
    val backgroundColor by animateColorAsState(
        targetValue = if (reaction.reactedByMe) {
            colors.accent.copy(alpha = 0.15f)
        } else {
            colors.card.copy(alpha = 0.5f)
        },
        animationSpec = swetTween(Motion.fast, SwetEasing.enter),
        label = "reactionBackground",
    )
    
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(Radius.full))
            .background(backgroundColor)
            .swetPressable(
                onClick = onClick,
                feedback = SwetPressFeedback.Scale
            )
            .padding(horizontal = Space.one, vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = reaction.emoji,
            fontSize = 14.sp
        )
        if (reaction.count > 1) {
            Text(
                text = reaction.count.toString(),
                style = MaterialTheme.typography.labelSmall.copy(fontSize = 11.sp),
                color = if (reaction.reactedByMe) colors.accent else colors.textSecondary
            )
        }
    }
}
